import { format, parseISO } from 'date-fns';
import type { ScenarioResult } from '../../../types';
import { formatCurrency } from './formatters';

export function getChartData(results: ScenarioResult, animationProgress: number) {
  const getVisibleData = (data: number[]) => {
    const visiblePoints = Math.floor(data.length * animationProgress);
    return data.slice(0, visiblePoints);
  };

  return {
    labels: results.dates.map(date => format(parseISO(date), 'MMM yyyy')),
    datasets: [
      {
        label: 'Conservative Strategy',
        data: getVisibleData(results.risk_free),
        borderColor: 'rgb(107, 114, 128)',
        backgroundColor: 'rgb(107, 114, 128)',
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 2
      },
      {
        label: 'Low Risk Strategy',
        data: getVisibleData(results.low_risk),
        borderColor: 'rgb(37, 99, 235)',
        backgroundColor: 'rgb(37, 99, 235)',
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 2
      },
      {
        label: 'Moderate Risk Strategy',
        data: getVisibleData(results.moderate_risk),
        borderColor: 'rgb(22, 163, 74)',
        backgroundColor: 'rgb(22, 163, 74)',
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 2
      },
      {
        label: 'Higher Risk Strategy',
        data: getVisibleData(results.high_risk),
        borderColor: 'rgb(234, 88, 12)',
        backgroundColor: 'rgb(234, 88, 12)',
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 2
      }
    ]
  };
}

export function getChartOptions(results: ScenarioResult) {
  const getAxisBounds = () => {
    const allValues = [
      ...results.risk_free,
      ...results.low_risk,
      ...results.moderate_risk,
      ...results.high_risk
    ];
    const max = Math.max(...allValues);
    const min = Math.min(...allValues);
    const range = max - min;
    
    return {
      min: Math.max(0, min - (range * 0.1)),
      max: max + (range * 0.1)
    };
  };

  const bounds = getAxisBounds();

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    animation: {
      duration: 0
    },
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Reserve Fund Growth Projections',
      },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.parsed.y;
            return `${context.dataset.label}: ${formatCurrency(value)}`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: false,
        min: bounds.min,
        max: bounds.max,
        ticks: {
          callback: (value: number) => formatCurrency(value)
        }
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          maxRotation: 45,
          minRotation: 45,
        },
      },
    },
  };
}